"use client";

import { usePathname, useRouter } from "@/lib/navigation";
import { useEffect } from "react";
import { useLocaleSync } from "@/hooks/use-locale-sync";
import { useTranslation } from "@/hooks/use-translations";
import { useAppStore } from "@/lib/store/app";
import {
  CHUNK_RECOVERY_SESSION_KEY,
  clearChunkRecoveryCaches,
  isChunkLoadError,
} from "./chunk-recovery";

// The inspection reports render from the device copy, so they open offline
// even when the failing inspection page fails again on every Try again.
const INSPECTIONS_PATH = "/inspections";
const INSPECTION_REPORTS_HREF = `${INSPECTIONS_PATH}?tab=reports`;

function isInInspectionApp(pathname: string) {
  return (
    pathname === INSPECTIONS_PATH || pathname.startsWith(`${INSPECTIONS_PATH}/`)
  );
}

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

// The router's error component for every route match. Reporting happens in the
// router's `defaultOnCatch`, once per caught error, not here.
export default function ErrorPage({ error, reset }: ErrorProps) {
  // A failing root never commits RootContent's locale sync, so the screen
  // reconciles the locale itself; under a committed root this is a no-op.
  useLocaleSync();
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useTranslation();
  const setLanguageLoading = useAppStore((state) => state.setLanguageLoading);
  const chunkLoadError = isChunkLoadError(error);

  useEffect(() => {
    setLanguageLoading(false);
  }, [setLanguageLoading]);

  useEffect(() => {
    if (!chunkLoadError || typeof window === "undefined") {
      return;
    }

    const hasAutoRecovered =
      window.sessionStorage.getItem(CHUNK_RECOVERY_SESSION_KEY) === "1";
    if (hasAutoRecovered) {
      return;
    }

    const recoverFromChunkError = async () => {
      window.sessionStorage.setItem(CHUNK_RECOVERY_SESSION_KEY, "1");
      try {
        await clearChunkRecoveryCaches();
      } catch (cacheError) {
        console.error(
          "Failed to clear runtime caches for chunk recovery:",
          cacheError,
        );
      } finally {
        window.location.reload();
      }
    };

    void recoverFromChunkError();
  }, [chunkLoadError]);

  const handleRetry = async () => {
    if (!chunkLoadError) {
      reset();
      return;
    }

    try {
      await clearChunkRecoveryCaches();
    } catch (cacheError) {
      console.error(
        "Failed to clear runtime caches on manual retry:",
        cacheError,
      );
    }
    window.location.reload();
  };

  return (
    <div
      data-testid="error-recovery"
      className="min-h-full flex items-center justify-center px-4"
    >
      <div className="max-w-lg w-full text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">
          {t("common.error.somethingWentWrong")}
        </h1>

        <div className="mb-8">
          <p className="text-gray-600 mb-4">
            {t("common.error.unexpectedError")}
          </p>

          {process.env.NODE_ENV === "development" && (
            <div className="text-left bg-gray-100 p-4 rounded-md mb-4">
              <p className="font-mono text-sm text-gray-800 break-all">
                {error.stack}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-4">
          <button
            data-testid="error-recovery-retry"
            onClick={() => void handleRetry()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            {t("common.error.tryAgain")}
          </button>

          <button
            data-testid="error-recovery-home"
            onClick={() => router.push("/")}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            {t("common.error.goHome")}
          </button>

          {isInInspectionApp(pathname) && (
            <button
              data-testid="error-recovery-inspections"
              onClick={() => router.push(INSPECTION_REPORTS_HREF)}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
            >
              {t("common.error.backToInspections")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
